import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'

import { Embed, File, Image } from '@tribeplatform/gql-client/global-types'
import { ClientError } from '@tribeplatform/gql-client/lib'
import {
  EditMessageInput,
  Message,
  MessageList,
  QueryDirection,
} from '@tribeplatform/gql-client/types'

import {
  UseMutationOptions,
  useMutation,
} from '../../lib/react-query/useMutation.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getMessagesKey } from '../../utils/keys/index.js'

interface MutateReturnType {
  previousMessages: InfiniteData<MessageList> | undefined
  exactQueryKey: QueryKey | undefined
}

type Input = EditMessageInput & {
  attachments?: File[]
  images?: Image[]
  embeds?: Embed[]
}

type Args = {
  chatId: string
  originalMessage: Message
  input: Input
}

export const useEditMessage = (
  options?: UseMutationOptions<Message, ClientError, Args>,
) => {
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<Message, ClientError, Args>(
    async (args: Args) => {
      return client.messaging.editMessage({
        chatId: args.chatId,
        input: {
          content: args.input.content,
          messageId: args.input.messageId,
          pageId: args.input.pageId,
          attachmentIds: args.input.attachmentIds,
          embedIds: args.input.embedIds,
          imageIds: args.input.imageIds,
        },
      })
    },
    {
      onMutate: args => {
        const notExactQueryKey = getMessagesKey({
          variables: {
            chatId: args.chatId,
          },
        })

        const getQueryData = (direction: QueryDirection) => {
          const [queryData] = queryClient.getQueriesData<
            InfiniteData<MessageList>
          >({
            exact: false,
            queryKey: notExactQueryKey,
            predicate(query) {
              return (
                query.queryHash?.includes(direction) &&
                query.queryHash?.includes(args.chatId)
              )
            },
          })

          const [exactQueryKey, messagesData] = queryData

          return {
            exactQueryKey,
            messagesData,
          }
        }

        const isMessageInData = (messagesList: InfiniteData<MessageList>) => {
          return messagesList.pages.some(page =>
            page.data.some(message => message._id === args.originalMessage._id),
          )
        }
        const updateMessagesPages = (
          messagesList: InfiniteData<MessageList>,
        ) => {
          return messagesList.pages.map(page => {
            const hasMessage = page.data.find(
              msg => msg._id === args.originalMessage._id,
            )
            if (!hasMessage) {
              return page
            }

            return {
              ...page,
              data: page.data.map(message => {
                if (message._id === args.originalMessage._id) {
                  return {
                    ...message,
                    ...args.input,
                    editedAt: new Date().toISOString(),
                  }
                }

                return message
              }),
            }
          })
        }

        const updateQueryData = (
          exactQueryKey: QueryKey,
          messagesData: InfiniteData<MessageList>,
        ) => {
          const updatedPages = updateMessagesPages(messagesData)
          queryClient.setQueryData<InfiniteData<MessageList>>(exactQueryKey, {
            ...messagesData,
            pages: updatedPages,
          })
        }

        const getQueryAndDataToUpdate = () => {
          // For each chat we have two queries, one for before and one for after
          // the last read message. The message to be edited can be in either of the two.
          const {
            exactQueryKey: exactQueryKeyBefore,
            messagesData: messagesDataBefore,
          } = getQueryData(QueryDirection.Before)
          if (isMessageInData(messagesDataBefore)) {
            updateQueryData(exactQueryKeyBefore, messagesDataBefore)
            return {
              previousMessages: messagesDataBefore,
              exactQueryKey: exactQueryKeyBefore,
            } satisfies MutateReturnType
          }

          const {
            exactQueryKey: exactQueryKeyAfter,
            messagesData: messagesDataAfter,
          } = getQueryData(QueryDirection.After)

          if (isMessageInData(messagesDataAfter)) {
            updateQueryData(exactQueryKeyAfter, messagesDataAfter)
            return {
              previousMessages: messagesDataAfter,
              exactQueryKey: exactQueryKeyAfter,
            } satisfies MutateReturnType
          }

          return {
            previousMessages: undefined,
            exactQueryKey: undefined,
          } satisfies MutateReturnType
        }

        const { previousMessages, exactQueryKey } = getQueryAndDataToUpdate()

        if (previousMessages && exactQueryKey) {
          updateQueryData(exactQueryKey, previousMessages)
        }

        if (options?.onMutate) {
          options.onMutate(args)
        }

        return {
          previousMessages,
          exactQueryKey,
        }
      },
      onSuccess: (_, args) => {
        const notExactQueryKey = getMessagesKey({
          variables: {
            chatId: args.chatId,
          },
        })
        queryClient.invalidateQueries({
          exact: false,
          queryKey: notExactQueryKey,
          predicate(query) {
            return query.queryHash.includes(args.chatId)
          },
        })
      },
      ...(options ?? {}),
      onError: (_, __, context: MutateReturnType) => {
        if (context.previousMessages && context.exactQueryKey) {
          queryClient.setQueryData<InfiniteData<MessageList>>(
            context.exactQueryKey,
            context.previousMessages,
          )
        }

        if (options?.onError) {
          options.onError(_, __, context)
        }
      },
    },
  )
}
